import { HfInference } from '@huggingface/inference';
import { storageService } from './storageService';
import { errorTrackingService } from './errorTrackingService';
import { databaseService } from './databaseService';

// Initialize HuggingFace client
const hfClient = new HfInference(import.meta.env.VITE_HUGGING_FACE_HUB_TOKEN || '');

// Model configuration
const FLUX_MODEL = 'black-forest-labs/FLUX.1-dev';

// Check if we have a valid API key
const hasValidApiKey = (): boolean => {
  const token = import.meta.env.VITE_HUGGING_FACE_HUB_TOKEN;
  return token !== null && 
         token !== undefined &&
         token.length > 10;
};

export interface ImageGenerationParams {
  prompt: string;
  userId: string;
  style?: string;
  options?: {
    width?: number;
    height?: number;
    steps?: number;
    seed?: number;
    n?: number; // Number of images to generate
  };
}

export interface ImageGenerationResult {
  imageUrl: string;
  prompt: string;
  model: string;
  cached: boolean;
}

class FluxImageService {
  private rateLimitMap = new Map<string, number>();
  private readonly RATE_LIMIT_WINDOW = 60000; // 1 minute
  private readonly MAX_REQUESTS_PER_MINUTE = 30; // Generous limit for free tier

  /**
   * Generate an image using Together AI's FLUX.1 [schnell] model
   */
  async generateImage(params: ImageGenerationParams): Promise<ImageGenerationResult> {
    const { prompt, userId, style, options = {} } = params;

    // The FLUX.1-schnell-Free model doesn't require an API key
    // if (!hasValidApiKey()) {
    //   throw new Error('[TogetherAI] No valid API key found. Please set VITE_TOGETHER_API_KEY in your .env.local file');
    // }

    // Check rate limiting
    if (!this.checkRateLimit(userId)) {
      throw new Error('[TogetherAI] Rate limit exceeded. Please wait a moment before generating more images.');
    }

    // Create a consistent cache key based on prompt
    const promptHash = btoa(prompt).replace(/[^a-zA-Z0-9]/g, '').substring(0, 32);
    const cacheKey = `hf_flux_${promptHash}`;
    
    // Check Supabase cache first
    try {
      const cachedUrl = await storageService.getCachedImage(cacheKey);
      if (cachedUrl) {
        console.log('[TogetherAI] Using cached image for prompt:', prompt);
        return {
          imageUrl: cachedUrl,
          prompt,
          model: FLUX_MODEL,
          cached: true
        };
      }
    } catch (cacheError) {
      console.warn('[TogetherAI] Cache check failed:', cacheError);
    }

    // Enhance prompt with style if provided
    const enhancedPrompt = style 
      ? `${prompt}, ${style}, high quality, detailed`
      : `${prompt}, high quality, detailed`;

    // Default options for FLUX.1 [schnell]
    const imageOptions = {
      width: options.width || 768,
      height: options.height || 512,
      steps: options.steps || 4, // FLUX schnell is optimized for 4 steps
      n: options.n || 1,
      ...options
    };

    try {
      console.log('[HF] Generating image with prompt:', enhancedPrompt);
      
      // Use HuggingFace Inference Client directly
      const blob = await hfClient.textToImage({
        model: FLUX_MODEL,
        inputs: enhancedPrompt,
        parameters: {
          num_inference_steps: imageOptions.steps || 5,
          width: imageOptions.width,
          height: imageOptions.height
          // FLUX.1-Krea-dev doesn't require guidance_scale to be 0.0
        }
      });

      console.log('[HF] Image generated successfully');

      // Log API usage
      await databaseService.logApiUsage('huggingface', FLUX_MODEL, 1, 0);

      // Cache the image by converting Blob to base64 for Supabase storage
      try {
        const cachedUrl = await this.cacheImageToSupabase(blob, cacheKey, {
          prompt: enhancedPrompt,
          userId,
          generatedAt: new Date().toISOString(),
          type: style ? style : 'scene',
          model: FLUX_MODEL
        });

        if (cachedUrl) {
          console.log('[HF] Image cached successfully to Supabase:', cachedUrl);
          return {
            imageUrl: cachedUrl,
            prompt: enhancedPrompt,
            model: FLUX_MODEL,
            cached: false
          };
        }
      } catch (cacheError) {
        console.warn('[HF] Failed to cache image to Supabase:', cacheError);
        // Continue to fallback below
      }

      // Fallback: create managed object URL with auto-cleanup
      console.warn('[HF] Using managed object URL fallback with auto-cleanup');
      const objectUrl = this.createManagedObjectUrl(blob);
      return {
        imageUrl: objectUrl,
        prompt: enhancedPrompt,
        model: FLUX_MODEL,
        cached: false
      };

    } catch (error) {
      console.error('[TogetherAI] Image generation failed:', error);
      errorTrackingService.logError(error as Error, {
        action: 'generateImage'
      });
      throw error;
    }
  }

  /**
   * Cache Blob image to Supabase storage with proper base64 conversion
   * Fixes: Blob objects can't be directly uploaded to Supabase
   */
  private async cacheImageToSupabase(
    blob: Blob,
    cacheKey: string,
    metadata: {
      prompt: string;
      userId: string;
      generatedAt: string;
      type: string;
      model: string;
    }
  ): Promise<string | null> {
    try {
      const { supabaseClient } = await import('./supabaseClient');

      // Convert Blob to ArrayBuffer then to base64
      const arrayBuffer = await blob.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);

      // Create file name with cache key
      const fileName = `${cacheKey}.png`;

      // Upload to Supabase storage
      const { data, error } = await supabaseClient.storage
        .from('image-cache')
        .upload(fileName, uint8Array, {
          contentType: 'image/png',
          upsert: true, // Allow overwriting existing files
          cacheControl: '3600', // Cache for 1 hour
        });

      if (error) {
        console.error('[FluxImageService] Supabase cache error:', error);

        // Check if error is RLS policy related
        if (error.message.toLowerCase().includes('policy') ||
            error.message.toLowerCase().includes('permission')) {
          console.error('[FluxImageService] ⚠️ RLS policy blocking upload. Run migration: database-migrations/20251113_image_cache_rls_policies.sql');
        }

        return null;
      }

      // Get public URL
      const { data: urlData } = supabaseClient.storage
        .from('image-cache')
        .getPublicUrl(fileName);

      console.log('[FluxImageService] ✅ Cached to Supabase:', urlData.publicUrl);
      return urlData.publicUrl;

    } catch (error) {
      console.error('[FluxImageService] Cache failed:', error);
      return null;
    }
  }

  /**
   * Object URL registry for memory management
   * Tracks created URLs and auto-revokes them to prevent memory leaks
   */
  private objectUrlRegistry = new Map<string, NodeJS.Timeout>();

  /**
   * Create managed object URL with automatic cleanup
   * Fixes: Memory leaks from unrevoked object URLs
   */
  private createManagedObjectUrl(blob: Blob, ttl: number = 300000): string {
    const url = URL.createObjectURL(blob);

    // Schedule automatic cleanup after TTL (default 5 minutes)
    const timeoutId = setTimeout(() => {
      URL.revokeObjectURL(url);
      this.objectUrlRegistry.delete(url);
      console.log('[FluxImageService] Auto-revoked object URL:', url.substring(0, 50) + '...');
    }, ttl);

    this.objectUrlRegistry.set(url, timeoutId);

    console.log('[FluxImageService] Created managed object URL (will auto-revoke in ' + (ttl / 1000) + 's)');
    return url;
  }

  /**
   * Manually revoke an object URL before TTL expires
   */
  public revokeObjectUrl(url: string): void {
    const timeoutId = this.objectUrlRegistry.get(url);
    if (timeoutId) {
      clearTimeout(timeoutId);
      URL.revokeObjectURL(url);
      this.objectUrlRegistry.delete(url);
      console.log('[FluxImageService] Manually revoked object URL');
    }
  }

  /**
   * Cleanup all managed object URLs (call on app shutdown)
   */
  public cleanup(): void {
    console.log(`[FluxImageService] Cleaning up ${this.objectUrlRegistry.size} object URLs`);
    for (const [url, timeoutId] of this.objectUrlRegistry.entries()) {
      clearTimeout(timeoutId);
      URL.revokeObjectURL(url);
    }
    this.objectUrlRegistry.clear();
  }

  /**
   * Check if user is within rate limits
   */
  private checkRateLimit(userId: string): boolean {
    const now = Date.now();
    const userRequests = this.rateLimitMap.get(userId) || 0;
    
    // Clean up old entries
    for (const [key, timestamp] of this.rateLimitMap.entries()) {
      if (now - timestamp > this.RATE_LIMIT_WINDOW) {
        this.rateLimitMap.delete(key);
      }
    }

    if (userRequests >= this.MAX_REQUESTS_PER_MINUTE) {
      return false;
    }

    this.rateLimitMap.set(userId, userRequests + 1);
    return true;
  }

  /**
   * Test the Together AI connection
   */
  async testConnection(): Promise<boolean> {
    if (!hasValidApiKey()) {
      console.warn('[TogetherAI] No API key configured');
      return false;
    }

    try {
      const testParams: ImageGenerationParams = {
        prompt: 'A simple test image of a cube',
        userId: 'test-user',
        options: {
          width: 256,
          height: 256,
          steps: 1
        }
      };

      const result = await this.generateImage(testParams);
      return !!result.imageUrl;
    } catch (error) {
      console.error('[TogetherAI] Connection test failed:', error);
      return false;
    }
  }

  /**
   * Get API key status
   */
  getApiKeyStatus(): 'exists' | 'not_exists' {
    return hasValidApiKey() ? 'exists' : 'not_exists';
  }
}

// Export singleton instance
export const fluxImageService = new FluxImageService();
